import React, { useContext, useEffect, useRef, useState } from 'react';
import './BillingDate.css';
import addDays from 'date-fns/addDays';
import isSameDay from 'date-fns/isSameDay';
import parseISO from 'date-fns/parseISO';
import startOfDay from 'date-fns/startOfDay';
import setHours from 'date-fns/setHours';
import setMinutes from 'date-fns/setMinutes';
import { AccountContext } from '../account/AccountContext';
import { isAfter, isBefore } from 'date-fns';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { formatDate } from '@app/utilities';
import {
  useCurrentBillingPeriod,
  useRefreshCurrentBillingPeriod,
  useRefreshAccountId,
  useRefreshGetNosiasForAccount,
  useRefreshConsumerBillingService
} from '@app/sdk';
import { useAccountIdContext } from '../account/AccountIdContext';
import { toast } from 'react-toastify';
import { PollingTimeoutError } from '@app/hooks';

export default function BillingDate() {
  const {
    billingDate,
    setBillingDate,
    accountId,
    eodoService,
    previousBillingPeriod,
    eodDate,
    setEodDate,
    isRunningEod,
    setIsRunningEod,
  } = useContext(AccountContext);
  const { data: currentBillingPeriod } = useCurrentBillingPeriod(accountId);
  const [isEndOfBillingPeriod, setIsEndOfBillingPeriod] = useState(false);
  const [isAfterPaymentDueDate, setIsAfterPaymentDueDate] = useState(false);
  const [runEndOfDayWhileTimeTraveling, setRunEndOfDayWhileTimeTraveling] = useState(false);
  const [changeTime, setChangeTime] = useState(false);
  const [selectedTime, setSelectedTime] = useState<Date>(() => setMinutes(setHours(new Date(), 0), 45));
  const [endOfDayEndDate, setEodEndDate] = useState<Date | undefined>();
  const { isBillingDateOutbound, setIsBillingDateOutBound } = useAccountIdContext();
  const refreshCurrentBillingPeriod = useRefreshCurrentBillingPeriod();
  const refreshAccount = useRefreshAccountId(accountId);
  const refreshNosias = useRefreshGetNosiasForAccount(accountId);
  const refreshConsumerBillingService = useRefreshConsumerBillingService(accountId);
  const eodRunningRef = useRef(false);
  const [activeButton, setActiveButton] = useState<string | null>(null);

  useEffect(() => {
    setIsAfterPaymentDueDate(isAfter(billingDate, parseISO(previousBillingPeriod?.plannedPaymentDueDate)));
    if (currentBillingPeriod?.billingPeriodId) {
      setIsEndOfBillingPeriod(isSameDay(billingDate, parseISO(currentBillingPeriod?.endDate)));
    }
  }, [billingDate, currentBillingPeriod, previousBillingPeriod]);

  const endPeriod = async () => {
    if (eodRunningRef.current) return;
    eodRunningRef.current = true;
    setActiveButton('period');
    setIsRunningEod(true);

    try {
      if (currentBillingPeriod?.billingPeriodId > 1 && previousBillingPeriod.paymentDueDate) {
        await executeEod(parseISO(previousBillingPeriod.paymentDueDate));
      }
      await executeEod(parseISO(currentBillingPeriod?.endDate));
      await refreshConsumerBillingService();
      await refreshNosias();
    } catch (error) {
      handleEodError(error);
    } finally {
      eodRunningRef.current = false;
      setActiveButton(null);
      setIsRunningEod(false);
    }
  };

  function applyTime(date: Date): Date {
    if (!changeTime || !date) return date;
    return setMinutes(setHours(date, selectedTime.getHours()), selectedTime.getMinutes());
  }

  function updateBillingDate(newBillingDate: Date) {
    setBillingDate(newBillingDate);
    setIsBillingDateOutBound(false);
    localStorage.setItem(`${accountId}-billing-date`, newBillingDate.toISOString());
    refreshCurrentBillingPeriod();
  }

  function handleEodError(error: unknown) {
    const message = error instanceof PollingTimeoutError
      ? `EOD did not complete within the timeout period. Processing stopped to prevent data corruption.`
      : `EOD failed: ${error instanceof Error ? error.message : 'Unknown error'}`;
    toast.error(message);
    console.error('EOD error:', error);
  }

  async function executeEod(date: Date) {
    setEodDate(date);
    setEodEndDate(date);
    await eodoService.triggerEod(accountId, date);
    await eodoService.waitForEodCompletion(accountId, date);
    updateBillingDate(addDays(date, 1));
  }

  async function endDayForRange(eodEndDate: Date, startDate: Date) {
    if (eodRunningRef.current) return;
    eodRunningRef.current = true;

    let currentDate = startOfDay(startDate ?? eodEndDate);
    const normalizedEndDate = startOfDay(eodEndDate);
    setEodEndDate(eodEndDate);
    setIsRunningEod(true);

    try {
      while (isBefore(currentDate, normalizedEndDate) || isSameDay(currentDate, normalizedEndDate)) {
        setEodDate(currentDate);

        await eodoService.triggerEod(accountId, currentDate);
        await eodoService.waitForEodCompletion(accountId, currentDate);

        currentDate = addDays(currentDate, 1);
        updateBillingDate(currentDate);
      }
    } catch (error) {
      handleEodError(error);
    } finally {
      eodRunningRef.current = false;
      setIsRunningEod(false);
    }
  }

  async function endDay(eodEndDate: Date, refresh = false, button = 'today') {
    if (eodRunningRef.current) return;
    eodRunningRef.current = true;
    setActiveButton(button);
    setIsRunningEod(true);

    try {
      await executeEod(eodEndDate);

      if (refresh) {
        await refreshAccount();
        await refreshConsumerBillingService()
        await refreshNosias();
      }
    } catch (error) {
      handleEodError(error);
    } finally {
      eodRunningRef.current = false;
      setActiveButton(null);
      setIsRunningEod(false);
    }
  }

  return (
    <div className={'ui grid'}>
      <div className="ten wide column">
        <div className="row">
          <h3 style={{ color: `${isBillingDateOutbound ? 'red' : ''}` }}>
            Current Date: {formatDate(billingDate)}
            {isBillingDateOutbound && ' (Out of current billing period)'}
          </h3>
        </div>
        {isRunningEod && endOfDayEndDate ? (
          <div className={'row'}>
            <h3>
              Running end of day {isSameDay(eodDate, endOfDayEndDate) ? 'for' : 'to'} {formatDate(endOfDayEndDate)}
            </h3>
          </div>
        ) : (
          <div className={'row margin-1 ui form date-selector'}>
            <strong>
              {runEndOfDayWhileTimeTraveling ? 'Run end of day until: ' : 'Time travel to: '}
            </strong>
            <DatePicker
            className="date-picker"
              selected={billingDate}
              onChange={(date) => {
                const dateWithTime = applyTime(date);
                runEndOfDayWhileTimeTraveling ? endDayForRange(dateWithTime, billingDate) : updateBillingDate(dateWithTime);
              }}
              dateFormat="yyyy-MM-dd"
              minDate={runEndOfDayWhileTimeTraveling ? addDays(billingDate, 1) : undefined}
              includeDateIntervals={
                runEndOfDayWhileTimeTraveling
                  ? undefined
                  : [
                      {
                        start: new Date(parseISO(currentBillingPeriod?.startDate) ?? billingDate),
                        end: new Date(parseISO(currentBillingPeriod?.endDate) ?? billingDate),
                      },
                    ]
              }
              disabled={isRunningEod}>
              <div className="ui labeled input">
                <div className="ui checkbox">
                  <input
                    type="checkbox"
                    id={'setRunEndOfDayWhileTimeTraveling'}
                    checked={runEndOfDayWhileTimeTraveling}
                    onChange={(e) => setRunEndOfDayWhileTimeTraveling(!runEndOfDayWhileTimeTraveling)}
                  />
                  <label htmlFor={'setRunEndOfDayWhileTimeTraveling'}>Run end of day(s)</label>
                </div>
              </div>
              <div className="ui labeled input">
                <div className="ui checkbox">
                  <input
                    type="checkbox"
                    id={'setChangeTime'}
                    checked={changeTime}
                    onChange={(e) => setChangeTime(!changeTime)}
                  />
                  <label htmlFor={'setChangeTime'}>Change time</label>
                </div>
                {changeTime && (
                  <DatePicker
                    className="date-picker time-picker"
                    selected={selectedTime}
                    onChange={(time) => setSelectedTime(time)}
                    showTimeSelect
                    showTimeSelectOnly
                    timeIntervals={1}
                    timeCaption="Time"
                    dateFormat="HH:mm"
                  />
                )}
              </div>
            </DatePicker>
          </div>
        )}
        <div className="row">
          {!isEndOfBillingPeriod && (
            <button className="ui primary button" onClick={() => endDay(billingDate)} disabled={isRunningEod}>
              {activeButton === 'today' && <i className="notched circle loading icon" />}
              End Today
            </button>
          )}
          {!isAfterPaymentDueDate && currentBillingPeriod?.billingPeriodId != 1 && (
            <button
              className="ui primary button"
              onClick={() => endDay(parseISO(previousBillingPeriod.plannedPaymentDueDate), true, 'dueday')}
              disabled={isRunningEod}>
              {activeButton === 'dueday' && <i className="notched circle loading icon" />}
              End Due Day
            </button>
          )}
          <button className="ui primary button" onClick={() => endPeriod()} disabled={isRunningEod}>
            {activeButton === 'period' && <i className="notched circle loading icon" />}
            End Period
          </button>
        </div>
      </div>
    </div>
  );
}
